import type { CompetitorTypes } from "~enums/competitorTypes";
import type { FixtureResultStatuses } from "~enums/fixtureResultStatuses";
import type { FixtureStatuses } from "~enums/fixtureStatuses";
import type { EditFixturePageModel, FixtureEntityTransformed, FixturePersonTransformed } from "~pages/Fixtures/types";
import type { Entity } from "~types/entity";
import type { Fixture, FixtureResources, FixtureResponse } from "~types/fixture";
import type { FixtureEntity } from "~types/fixtureEntity";
import type { FixturePerson } from "~types/fixturePerson";
import type { Person } from "~types/person";

export const transformFixtureResponse = (response: FixtureResponse): EditFixturePageModel => {
  const fixture: Fixture | undefined = response?.data?.[0];
  if (!fixture) {
    throw "Fixture does not exist in the fixture response";
  }

  const resources: FixtureResources | undefined = response.includes?.resources;
  const competitorType = fixture.competitorType as CompetitorTypes;
  const competitors = fixture.competitors;
  const participants = fixture.participants;
  const entrants = (competitors?.length ? competitors : participants) ?? [];

  const createFixtureEntityTransformed = (fixtureEntity: FixtureEntity, entity?: Entity): FixtureEntityTransformed => {
    if (!entity) {
      throw new Error("Entity should be returned with FixtureEntity");
    }

    const uniform = resources?.uniforms?.[fixtureEntity?.uniformId ?? ""];

    return {
      externalId: fixtureEntity.externalId || "",
      entityId: fixtureEntity.entityId,
      colors: {
        primary: entity.colors?.primary || "",
        secondary: entity.colors?.secondary || "",
        tertiary: entity.colors?.tertiary || "",
      },
      includeInRepresentation: fixtureEntity.includeInRepresentation ?? false,
      name: entity.nameFullLocal,
      nameAbbreviation: entity.codeLocal || entity.codeLatin || "",
      uniform: uniform ? { label: uniform.nameLocal, value: uniform.uniformId } : undefined,
      isHome: fixtureEntity.isHome ?? false,
      resultStatus: fixtureEntity.resultStatus as FixtureResultStatuses,
    };
  };

  const createFixturePersonTransformed = (fixturePerson: FixturePerson, person: Person | undefined): FixturePersonTransformed => {
    if (!person) {
      throw new Error("Person should be returned with FixturePerson");
    }

    const uniform = resources?.uniforms?.[fixturePerson?.uniformId ?? ""];

    return {
      externalId: fixturePerson.externalId || "",
      personId: fixturePerson.personId,
      includeInRepresentation: fixturePerson.includeInRepresentation ?? false,
      name: person.nameFullLocal,
      nameAbbreviation: person.nameAbbreviated ?? "",
      uniform: uniform ? { label: uniform.nameLocal, value: uniform.uniformId } : undefined,
      isHome: fixturePerson.isHome ?? false,
      resultStatus: fixturePerson.resultStatus as FixtureResultStatuses,
    };
  };

  const transformScore = (value?: string | null): string => {
    const isEmptyString = value === "" || typeof value === "undefined" || value === null;
    if (isEmptyString) return "";
    const number = Number(value);
    return Number.isNaN(number) ? "" : String(number);
  };

  const competitorsTransformed =
    fixture.competitors?.map((x) => createFixtureEntityTransformed(x, resources?.entities?.[x.entity?.id])) ?? [];
  const participantsTransformed =
    fixture.participants?.map((x) => createFixturePersonTransformed(x, resources?.persons?.[x.person?.id])) ?? [];

  const selectedStage = resources?.[fixture.stage?.resourceType || ""]?.[fixture.stage?.id || ""];
  const selectedPool = resources?.[fixture.pool?.resourceType || ""]?.[fixture.pool?.id || ""];
  const selectedSeries = resources?.[fixture.series?.resourceType || ""]?.[fixture.series?.id || ""];
  const selectedFixtureProfile = resources?.[fixture.fixtureProfile?.resourceType || ""]?.[fixture.fixtureProfile?.id || ""];

  return {
    competitorType: competitorType,
    formData: {
      added: fixture.added.replace("T", " "),
      attendance: String(fixture.attendance || ""),
      externalId: fixture.externalId || "",
      fixtureNumber: String(fixture.fixtureNumber || ""),
      includeInConferenceStatistics: fixture.competitors?.[0]?.includeInConferenceStatistics ?? false,
      includeInStandings: !!fixture.includeInStandings,
      internationalReference: fixture.internationalReference || "",
      isNeutralVenue: entrants[0]?.isNeutralVenue ?? false,
      liveDataAvailable: !!fixture.liveDataAvailable,
      liveVideoAvailable: !!fixture.liveVideoAvailable,
      nameLatin: fixture.nameLatin || "",
      nameLocal: fixture.nameLocal || "",
      placingIfLost: String(fixture.placingIfLost || ""),
      placingIfWon: String(fixture.placingIfWon || ""),
      poolCode: selectedPool ? { label: selectedPool.nameLocal, value: selectedPool.poolCode } : undefined,
      profileId: selectedFixtureProfile
        ? {
            label: selectedFixtureProfile.name,
            value: selectedFixtureProfile.profileId,
          }
        : undefined,
      roundNumber: fixture.roundNumber || "",
      homeScore: transformScore(entrants.filter((x) => x.isHome)[0]?.score),
      awayScore: transformScore(entrants.filter((x) => !x.isHome)[0]?.score),
      homeSecondaryScore: transformScore(entrants.filter((x) => x.isHome)[0]?.secondaryScore),
      awaySecondaryScore: transformScore(entrants.filter((x) => !x.isHome)[0]?.secondaryScore),
      sellout: !!fixture.sellout,
      seriesCode: selectedSeries ? { label: selectedSeries.nameLocal, value: selectedSeries.seriesCode } : undefined,
      stageCode: selectedStage ? { label: selectedStage.nameLocal, value: selectedStage.stageCode } : undefined,
      startTimeUTC: fixture.startTimeUTC?.replace("T", " ") || "",
      status: fixture.status as FixtureStatuses,
      ticketURL: fixture.ticketURL || "",
      timesUnconfirmed: !!fixture.timesUnconfirmed,
      updated: fixture.updated.replace("T", " "),
      competitors: competitorsTransformed,
      participants: participantsTransformed,
    },
  };
};
